import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Gift, Check } from "@phosphor-icons/react";

const TYPES = [
  { key: "nightwear", label: "Intimate Nightwear", sub: "Sheer & satin pieces" },
  { key: "roleplay", label: "Role Play", sub: "Curated character sets" },
];

export default function GiftAddOn({ onGiftChange }) {
  const [enabled, setEnabled] = useState(false);
  const [type, setType] = useState(null);
  const [options, setOptions] = useState([]);
  const [selected, setSelected] = useState(null);
  const [size, setSize] = useState("");
  const [color, setColor] = useState("");
  const [note, setNote] = useState("");
  const [recipientName, setRecipientName] = useState("");
  const [recipientEmail, setRecipientEmail] = useState("");

  useEffect(() => {
    if (!type) return;
    api.get("/products", { params: { category: type } })
      .then(r => { setOptions(r.data); setSelected(null); })
      .catch(() => setOptions([]));
  }, [type]);

  useEffect(() => {
    if (!selected) return;
    setSize(selected.sizes?.[0] || "");
    setColor(selected.colors?.[0] || "");
  }, [selected]);

  useEffect(() => {
    if (enabled && selected && size) {
      onGiftChange({
        product: selected,
        size,
        color,
        note: note.trim(),
        recipientName: recipientName.trim(),
        recipientEmail: recipientEmail.trim(),
      });
    } else {
      onGiftChange(null);
    }
  }, [enabled, selected, size, color, note, recipientName, recipientEmail, onGiftChange]);

  return (
    <div className="mt-8 border border-[#E6DED5] bg-[#FAF8F5]" data-testid="gift-addon">
      <button
        type="button"
        onClick={() => setEnabled(v => !v)}
        className="w-full flex items-center justify-between p-5 hover:bg-[#F0EBE1] transition-colors text-left"
        data-testid="gift-toggle"
      >
        <div className="flex items-center gap-3">
          <div className={`w-5 h-5 border-2 flex items-center justify-center flex-shrink-0 transition-colors ${enabled ? "border-[#4A1525] bg-[#4A1525]" : "border-[#1A1A1A]"}`}>
            {enabled && <Check size={12} weight="bold" color="#FAF8F5" />}
          </div>
          <div>
            <div className="font-serif text-lg leading-tight">Make it a gift</div>
            <div className="overline text-[#5C5C5C] text-[10px] mt-1">Pair the Ritual with an intimate piece or a role-play set</div>
          </div>
        </div>
        <Gift size={22} weight="light" className="text-[#4A1525]" />
      </button>

      {enabled && (
        <div className="p-5 pt-2 border-t border-[#E6DED5] space-y-6">
          {/* Type selector */}
          <div>
            <div className="overline mb-3">Choose an add-on</div>
            <div className="grid grid-cols-2 gap-3">
              {TYPES.map(t => (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => setType(t.key)}
                  className={`p-4 border text-left transition-colors ${type === t.key ? "border-[#4A1525] bg-[#4A1525] text-[#FAF8F5]" : "border-[#E6DED5] hover:border-[#1A1A1A]"}`}
                  data-testid={`gift-type-${t.key}`}
                >
                  <div className="font-serif text-lg leading-tight">{t.label}</div>
                  <div className={`overline text-[9px] mt-1 ${type === t.key ? "text-[#FAF8F5]/70" : "text-[#5C5C5C]"}`}>{t.sub}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Product picker */}
          {type && (
            <div>
              <div className="overline mb-3">Pick a piece</div>
              {options.length === 0 ? (
                <div className="text-xs text-[#5C5C5C] italic">Loading pieces…</div>
              ) : (
                <div className="grid grid-cols-3 gap-3">
                  {options.map(p => (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => setSelected(p)}
                      className="text-left group"
                      data-testid={`gift-option-${p.slug}`}
                    >
                      <div className={`aspect-[3/4] overflow-hidden mb-2 border-2 transition-colors ${selected?.id === p.id ? "border-[#4A1525]" : "border-transparent group-hover:border-[#E6DED5]"}`}>
                        <img src={p.images?.[0]} alt={p.name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                      </div>
                      <div className="font-serif text-xs leading-tight line-clamp-2">{p.name}</div>
                      <div className="text-xs text-[#5C5C5C] mt-0.5">₹{p.price.toFixed(0)}</div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Size / colour */}
          {selected && (
            <div className="space-y-4">
              {selected.colors?.length > 0 && (
                <div>
                  <div className="overline mb-2">Colour <span className="text-[#5C5C5C]">— {color}</span></div>
                  <div className="flex gap-2 flex-wrap">
                    {selected.colors.map(c => (
                      <button
                        type="button"
                        key={c}
                        onClick={() => setColor(c)}
                        className={`px-3 py-1.5 border text-xs transition-colors ${color === c ? "border-[#4A1525] bg-[#4A1525] text-[#FAF8F5]" : "border-[#E6DED5] hover:border-[#1A1A1A]"}`}
                        data-testid={`gift-color-${c}`}
                      >
                        {c}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {selected.sizes?.length > 0 && (
                <div>
                  <div className="overline mb-2">Size</div>
                  <div className="flex gap-2 flex-wrap">
                    {selected.sizes.map(s => (
                      <button
                        type="button"
                        key={s}
                        onClick={() => setSize(s)}
                        className={`px-3 py-1.5 border text-xs transition-colors ${size === s ? "border-[#4A1525] bg-[#4A1525] text-[#FAF8F5]" : "border-[#E6DED5] hover:border-[#1A1A1A]"}`}
                        data-testid={`gift-size-${s}`}
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Gift note */}
          <div>
            <div className="overline mb-2">A note (optional)</div>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value.slice(0, 200))}
              placeholder="A quiet line for them — enclosed on a linen card."
              className="w-full border border-[#E6DED5] bg-white p-3 text-sm font-serif italic focus:outline-none focus:border-[#4A1525] resize-none"
              rows={2}
              data-testid="gift-note"
            />
            <div className="text-[10px] text-[#5C5C5C] mt-1 tracking-wider uppercase">{note.length}/200 · Discreet handwritten card</div>
          </div>

          {/* Digital gift card — recipient details */}
          <div className="pt-4 border-t border-[#E6DED5]">
            <div className="overline mb-1">Send a digital card ahead</div>
            <p className="text-[10px] text-[#5C5C5C] mb-3 leading-relaxed">
              Optional. If you share their email, we'll send them a private note-card the moment your order is placed — the parcel arrives a few days later.
            </p>
            <div className="grid grid-cols-2 gap-3">
              <input
                type="text"
                value={recipientName}
                onChange={(e) => setRecipientName(e.target.value.slice(0, 60))}
                placeholder="Their name"
                className="border border-[#E6DED5] bg-white px-3 py-2 text-sm focus:outline-none focus:border-[#4A1525]"
                data-testid="gift-recipient-name"
              />
              <input
                type="email"
                value={recipientEmail}
                onChange={(e) => setRecipientEmail(e.target.value.slice(0, 120))}
                placeholder="Their email"
                className="border border-[#E6DED5] bg-white px-3 py-2 text-sm focus:outline-none focus:border-[#4A1525]"
                data-testid="gift-recipient-email"
              />
            </div>
          </div>

          {selected && size && (
            <div className="bg-white border border-[#E6DED5] px-4 py-3 text-xs text-[#5C5C5C] flex items-start gap-3" data-testid="gift-summary">
              <Gift size={16} weight="light" className="text-[#4A1525] mt-0.5 flex-shrink-0" />
              <div>
                Adding <span className="text-[#1A1A1A] font-serif italic">{selected.name}</span> ({size}{color ? `, ${color}` : ""}) as a gift with the Ritual. <span className="text-[#4A1525]">+ ₹{selected.price.toFixed(0)}</span>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
